// The home page's search state lives entirely in the URL. This module is the
// single reader of those params (the writer is buildSearchHref). Adding a
// param means touching both — nothing else parses the query string.
//
//   search   free text             loc        location label
//   gt       JSON array GroupType  pt         JSON array THERAPISTS|COACHES|ALTERNATIVE
//   v / ip   "true" flags          latitude   longitude   search origin
//   type     groups (default) | providers | organizations
//   page     1-based results page

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();export const SEARCH_TYPE_GROUPS = "groups"
export const SEARCH_TYPE_PROVIDERS = "providers"
export const SEARCH_TYPE_ORGANIZATIONS = "organizations"
export const SEARCH_TYPES = [
  SEARCH_TYPE_GROUPS,
  SEARCH_TYPE_PROVIDERS,
  SEARCH_TYPE_ORGANIZATIONS,
] as const
export type SearchType = (typeof SEARCH_TYPES)[number]

/** Cards per results page (all three tabs). */
export const PAGE_SIZE = 24

export type RawSearchParams =
  { [key: string]: string | string[] | undefined } | undefined | null

const first = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value

export const parseSearchType = (raw: unknown): SearchType => {
  const value = typeof raw === "string" ? raw.trim().toLowerCase() : ""
  return (SEARCH_TYPES as readonly string[]).includes(value)
    ? (value as SearchType)
    : SEARCH_TYPE_GROUPS
}

/** Positive integer page, else 1. */
export const parsePage = (raw: unknown): number => {
  const n = typeof raw === "string" ? Number.parseInt(raw, 10) : Number.NaN
  return Number.isInteger(n) && n > 0 ? n : 1
}

const parseJsonStringArray = (raw: string | undefined): string[] => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(decodeURIComponent(raw))
    return Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === "string")
      : []
  } catch {
    return []
  }
}

export function extractSearchParams(searchParams: RawSearchParams) {
  const sp = searchParams ?? {}
  const searchTerm = first(sp.search) || ""
  const location = first(sp.loc) || ""
  const groupTypes = parseJsonStringArray(first(sp.gt))
  const providerTypes = parseJsonStringArray(first(sp.pt))
  let virtual = first(sp.v) === "true"
  let inPerson = first(sp.ip) === "true"
  const latitude = parseFloat(first(sp.latitude) ?? "") || 0
  const longitude = parseFloat(first(sp.longitude) ?? "") || 0
  const type = parseSearchType(first(sp.type))
  const page = parsePage(first(sp.page))

  const isTherapyGroups = groupTypes.includes("THERAPY_GROUP")
  const isSupportGroups = groupTypes.includes("SUPPORT_GROUP")
  const isRetreats = groupTypes.includes("RETREAT")
  const isWorkshops = groupTypes.includes("WORKSHOP")
  const isProviderGroups = groupTypes.includes("PROVIDER_GROUP")
  const isCoaches = providerTypes.includes("COACHES")
  const isTherapists = providerTypes.includes("THERAPISTS")
  const isAlternative = providerTypes.includes("ALTERNATIVE")
  const areQueryParamsPresent = Boolean(
    searchTerm ||
    location ||
    groupTypes.length ||
    providerTypes.length ||
    virtual ||
    inPerson,
  )
  // The Providers / Organizations tabs are always a results list (browse
  // mode with no query); Groups shows the promoted carousels until a query
  // param is present.
  const isResultsMode = areQueryParamsPresent || type !== SEARCH_TYPE_GROUPS
  // if no query params present we will
  // automatically show in person and virtual as selected
  if (!areQueryParamsPresent) {
    virtual = true
    inPerson = true
  }
  return {
    searchTerm,
    location,
    groupTypes,
    providerTypes,
    virtual,
    inPerson,
    latitude,
    longitude,
    type,
    page,
    isTherapyGroups,
    isSupportGroups,
    isRetreats,
    isWorkshops,
    isProviderGroups,
    isCoaches,
    isTherapists,
    isAlternative,
    areQueryParamsPresent,
    isResultsMode,
  }
}

export type SearchState = ReturnType<typeof extractSearchParams>
